/**
 * Officer-facing crypto forensics glossary — single source for Documentation
 * and the header Help dialog (via CryptoFaqSection re-export).
 *
 * Keep LE tone: case-work definitions, not marketing FAQ copy.
 */
export const DOCUMENTATION_GLOSSARY: Record<string, string> = {
    'USDT-TRC20':
        'Tether (USDT) on the Tron network using the TRC-20 token standard. Dominant rail in India-facing crypto fraud — most cash-outs land here, not on Ethereum.',
    'USDT (Tether)':
        'Dollar-pegged stablecoin often used as the cash-out token in Indian fraud cases. Prefer naming the chain (USDT-TRC20 vs ERC-20) when writing evidence.',
    TRC20:
        'Token standard on Tron. Addresses start with T… (base58). Never force-lowercase Tron addresses — case is part of the identifier.',
    Tron:
        'Blockchain network (native token TRX) widely used for USDT transfers in Indian crypto fraud cases.',
    Hop:
        'One step in a fund trail: wallet A sends to wallet B. Multi-hop traces follow several hops until funds reach an exchange, mixer, or cash-out.',
    'Auto-Trace':
        'Workbench action that finds paths from a subject wallet toward known exchanges or entities and draws the gold trail on the canvas.',
    'Gold Path':
        'Highlighted trail on the graph showing the money journey Auto-Trace (or Follow the Money) found — the visual evidence path officers brief from.',
    'Follow the Money':
        'Canvas action that highlights the fund path toward cash-out / exchange endpoints using cached graph data (fast path query, not a full BFS).',
    'Risk Score (0.0–1.0)':
        'Automated risk rating for an address. 0.0 ≈ clean / known good; 1.0 ≈ critical. High scores surface in Inspector chips and can seed escalation.',
    Exposure:
        "Who an address has transacted with, often as a share of volume. High mixer or sanctioned exposure is a red flag even if the subject address itself is unlabeled.",
    Counterparty:
        'The other party in a transaction. High-risk counterparties raise the subject’s risk and appear in triage / Top Counterparties.',
    Clustering:
        'Grouping wallet addresses that likely belong to the same person or entity (shared control), similar to linking multiple phone numbers to one suspect.',
    'Mixer / Tumbler':
        "Service that mixes many users’ funds to hide origin. Treat mixer exposure as laundering signal — not proof of intent by itself.",
    'Peel Chain':
        'Laundering pattern where value is peeled off in successive smaller transfers across many wallets to frustrate tracing.',
    'DEX (Decentralized Exchange)':
        'Exchange with no central KYC desk (e.g. Uniswap). Swaps happen wallet-to-wallet via smart contracts — useful for obfuscation before cash-out.',
    CEX:
        'Centralised exchange (Binance, WazirX, …) — company-run, usually KYC’d. Common final cash-out / freeze target for LE notices.',
    P2P:
        'Peer-to-peer trading — buyers/sellers settle off-order-book (Telegram, exchange P2P desks) with UPI + crypto. Bridge between fiat identity and chain.',
    UPI:
        'India’s real-time bank payment ID (e.g. name@paytm). Linking UPI to a crypto wallet connects bank identity to on-chain activity.',
    'OFAC Sanctions':
        'US Treasury SDN list of sanctioned entities/wallets. Hits must be treated as critical compliance flags in the Workbench Intel tab.',
    'Pig Butchering':
        'Romance / investment scam: long trust-building (“fattening”), then full drain. Look for gradual inflows then sudden outflows to exchanges/P2P.',
    'Smurfing / Structuring':
        'Breaking large value into many small transfers to avoid reporting thresholds. Many small peers into one collector is a related mule pattern.',
    'Fan-In / Money Mule':
        'Many sources funneling funds into one address. The collector is often a mule who forwards proceeds toward the actual operator.',
    Bridge:
        'Cross-chain transfer mechanism. Funds leave one chain and appear on another — trail continues only if you pick up the destination chain.',
    'Hot wallet':
        'Exchange (or service) wallet kept online for customer withdrawals. High volume; useful as a stop entity when tracing cash-out.',
    'Cold storage':
        'Offline / low-activity custody wallets. Sudden activation after long dormancy can be suspicious (dormant activation).',
    Entity:
        'Named organisation or person attributed to a wallet (exchange, mixer, scam cluster). Attribution layers include category, tags, and audit sources.',
    'On-chain':
        'Visible on the public ledger (addresses, amounts, times) versus off-chain context (bank rails, Telegram, device, UPI).',
    'LE Report':
        'Officer-facing investigation report generated after Auto-Investigate — narrative plus appendices for case file / briefings.',
};
